import { Action } from 'com/gongxigames/math/core/game/enums/Action';
import { PromotionDecision, ResponseBody } from '@gongxigames/framework/integrations/rubyplay/types';
import { RubyPlayGameClientWrapper } from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapper';
import { RubyPlayGameClientWrapperSettings } from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import { dateDiffInSeconds } from '@gongxigames/framework/slot';
import {
    IMultiSetsMulticampaignPromotionInterface,
    PromotionTypes,
    MultiSetsPromotionInitData,
    PromotionBalanceExtractor,
    PromotionBalanceEntryQualifier,
    PromotionBalanceEntry,
    IPromotionalStatusesInterface,
} from '@gongxigames/framework/promotions';

const PAID_ROUND_FIELD = { paid_round: 1 };

type PromotionBalanceEntryFilter = (balance: PromotionBalanceEntry) => boolean;

export class MultiSetsMulticampaignPromotion implements IMultiSetsMulticampaignPromotionInterface, IPromotionalStatusesInterface {
    public static PING_BALANCE_DELAY = 3000;
    protected sets: PromotionBalanceEntry[] = [];
    protected expiredSets: PromotionBalanceEntry[] = [];
    protected currentSet?: PromotionBalanceEntry;
    private activePromotionId?: string;
    private clientWrapper!: RubyPlayGameClientWrapper;
    private defaultBet = -1;

    public getType(): PromotionTypes {
        return PromotionTypes.MultiSetsMulticampaignPromotion;
    }

    public init(data: MultiSetsPromotionInitData): void {
        const { initResponse, clientWrapper, defaultBet } = data;
        const { player, active_promotion_id } = initResponse.data;
        const promotionBalance = PromotionBalanceExtractor.extractFromPlayer(player);

        this.clientWrapper = clientWrapper;
        this.defaultBet = defaultBet;
        this.sets = this.getActivePromotion(promotionBalance, [
            this.isQueued.bind(this),
            this.isInUse.bind(this),
            this.isExpired.bind(this),
        ]);

        if (this.sets.length > 0) {
            if (this.currentSet === undefined) {
                this.currentSet = this.sets[0];
            }

            if (this.activePromotionId === undefined && active_promotion_id === this.currentSet.id) {
                this.activePromotionId = this.currentSet.id;
            }
        }
    }

    public update(response: ResponseBody): void {
        const { player, active_promotion_id } = response.data;

        this.sets = PromotionBalanceExtractor.extractFromPlayer(player);

        if (active_promotion_id) {
            this.activePromotionId = active_promotion_id;
        }

        const updatedSet = this.sets.find(({ id }) => this.currentSet?.id === id);

        if (this.currentSet && updatedSet) {
            this.currentSet = updatedSet;
        }
    }

    public getQualifier(): PromotionBalanceEntryQualifier {
        return this.getCurrentSet().type;
    }

    public hasPromotion(): boolean {
        return (
            this.getActivePromotion(this.sets, [this.isQueued.bind(this), this.isInUse.bind(this), this.isExpired.bind(this)]).length > 0
        );
    }

    public getPlayedRounds(): number {
        return this.getCurrentSet().awarded - this.getCurrentSet().remained;
    }

    public getRemainedRounds(): number {
        return this.getCurrentSet().remained;
    }

    public getTotalRounds(): number {
        return this.getCurrentSet().awarded;
    }

    public getExpirationTime(): string {
        return this.getCurrentSet().expirationTime ?? '';
    }

    public getRulesUrl(): string {
        return this.getCurrentSet().rulesUrl ?? '';
    }

    public isActive(): boolean {
        const set = this.currentSet;

        return set !== undefined && set.id === this.activePromotionId;
    }

    public isCurrentInUse(): boolean {
        return this.currentSet !== undefined && this.isInUse(this.currentSet);
    }

    public isCurrentExpired(): boolean {
        return this.currentSet !== undefined && this.isExpired(this.currentSet);
    }

    public subtractPromotion(): void {
        this.getCurrentSet().remained -= 1;
    }

    private getCurrentSet(): PromotionBalanceEntry {
        if (this.currentSet) {
            return this.currentSet;
        }

        throw new Error('No active free rounds set');
    }

    public endExpiredRounds(): void {
        if (this.currentSet && this.isExpired(this.currentSet)) {
            this.expiredSets.push(this.currentSet);
            this.end();
        }
    }

    public end(): void {
        this.sets = this.getNextAvailableSets();
        this.activePromotionId = undefined;
        this.currentSet = undefined;

        if (this.sets.length > 0) {
            this.currentSet = this.sets[0];
        }

        this.clientWrapper.updateDefaultBet();
    }

    protected getNextAvailableSets(): PromotionBalanceEntry[] {
        return this.getActivePromotion(this.sets, [this.isQueued.bind(this), this.isInUse.bind(this)])
            .filter(({id}) =>
                id !== this.currentSet?.id &&
                !this.expiredSets.some(({id: expiredId}) => expiredId === id)
            );
    }

    public setChoice(choice: PromotionDecision, succeessCB: () => void, failureCB: () => void = () => {}): void {
        this.clientWrapper.sendPromotionDecision(choice, () => {
            if (choice === 'accept') {
                this.expiredSets = [];
                this.activePromotionId = this.getCurrentSet().id;
                this.clientWrapper.updateDefaultBet();
            }

                succeessCB();
            },
            failureCB
        );
    }

    public getBet(): number {
        const subunit = this.clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().get('subunit');
        const wager = this.clientWrapper.wager;
        const bet = this.getCurrentSet().bet;

        if (!bet) return this.defaultBet;

        // Convert to subunits and round to nearest integer
        const betInSubunits = Math.round(bet * subunit);

        // Divide by wager if needed (depending on game math)
        const normalized = betInSubunits / wager;

        return normalized;
    }

    public getID(): string {
        return this.getCurrentSet().id;
    }

    public getWin(): number {
        const subunit = this.clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().get('subunit');

        return (this.getCurrentSet().collectedWin || 0) * subunit;
    }

    public getAdditionalFieldsForServerRequest(action: Action): Record<string, any> | undefined {
        const isPaidAction =
            action === Action.SPIN ||
            action === (Action as any).BUY_FEATURE;

        if (this.expiredSets.length > 0 && isPaidAction) {
            this.expiredSets = [];
            return PAID_ROUND_FIELD;
        }

        return undefined;
    }

    public getPingBalanceDelay(): number {
        return MultiSetsMulticampaignPromotion.PING_BALANCE_DELAY;
    }

    protected getActivePromotion(data: PromotionBalanceEntry[], conditions: PromotionBalanceEntryFilter[]): PromotionBalanceEntry[] {
        return data.filter((promotion) => {
            let passes = false;

            conditions.forEach((condition) => {
                passes = passes || condition(promotion);
            });

            if (passes) {
                return promotion;
            }
        });
    }

    public isQueued(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'QUEUED' && !this.isExpired(balance);
    }

    public isInUse(balance: PromotionBalanceEntry): boolean {
        const started = balance.status === 'IN_USE' || (balance.remained > 0 && balance.remained < balance.awarded);
        const expired = this.isExpired(balance);

        return started && !expired;
    }

    public isCompleted(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'COMPLETED' || balance.remained === 0;
    }

    public isExpired(balance: PromotionBalanceEntry): boolean {
        const isExpired = dateDiffInSeconds(Date.now(), new Date(`${balance.expirationTime}Z`).getTime()) < 0;

        return balance.status === 'EXPIRED' || isExpired;
    }

    public isOptOut(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'OPT_OUT';
    }

    public isDisabled(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'DISABLED';
    }
}
